import { useCompany } from 'context/company';
import { useUser } from 'context/user';
import {
  CORRIDOR_RATES_USER_IDS,
  GAURAVS_PROD_COMPANYID,
  ORG_CHART_PILOT_USER_IDS,
  PAYOUT_VOLUME_USER_IDS,
  THERA_PROD_COMPANY_ID,
} from 'lib/whitelistConstants';
import { ContractType, EmployeeType } from 'lib/constants';
import { useMemo } from 'react';
import { ManagerAccessType, WorkerAccessType } from 'routes';
import { getEligibleAdvanceContract } from 'utils/theraAdvance';
import { checkIfManagerWithoutTeams } from 'utils/affiliations';
import { isCheckCompanyRestricted } from 'utils/company';
import { canUserCreateBridgeWallet, isStytchProdEnv, isTheraDemoCompany } from 'utils/user';
import { isDateInPast } from 'utils/dateTime';
import { useCheckAllEarningRates, useCheckEmployeesSWR } from './useCheckEmployeeSWR';
import { useCheckCompanySWR } from './useCheckSWR';
import useContractorCompliance from './useContractorCompliance';
import { useMyWhiteLabelClients } from './useMyWhiteLabelClients';
import { usePermissions } from './usePermissions';

type NonNullManagerAccessType = Exclude<ManagerAccessType, null>;
export type AccessFlagKey = NonNullManagerAccessType | WorkerAccessType;
export type AccessFlags = Record<AccessFlagKey, boolean>;

export const useAccessRegistry = (): { flags: AccessFlags; isMyWLCLoading: boolean } => {
  const {
    user,
    roleType,
    affiliationsByCompany,
    currentCompany,
    currentContract,
    isLoading: isUserLoading,
  } = useUser() || {};
  const { company } = useCompany() || {};
  const { isOrgAdmin, isAPARManager, isHRSpecialist, canReviewInvoiceByTeamId, companyPermissions } = usePermissions();
  const { settings } = company || {};
  const isAPEnabled = settings?.enabledApps?.ap;
  const isAREnabled = settings?.enabledApps?.ar;
  const isBankingEnabled = settings?.enabledApps?.banking; // Banking app won't be enabled until it's added by user
  const isPayrollEnabled = settings?.enabledApps?.payroll;
  const isUSCompany = company?.address?.country === 'US';
  const { checkCompany } = useCheckCompanySWR({
    checkCompanyId: company?.w2Payroll?.checkId,
  });
  const { checkEmployees } = useCheckEmployeesSWR({
    checkCompanyId: company?.w2Payroll?.checkId,
  });
  const isOrgAdminOrHRSpecialist = isOrgAdmin || isHRSpecialist;
  const canUserCreateWallet = canUserCreateBridgeWallet(user?.employeeInformation?.address);

  // Get KYC and compliance info for the current user
  const { kycAndComplianceInfo } = useContractorCompliance(currentCompany?.companyID, user?.userID);
  const { complianceDocsEnabled, kycEnabled } = kycAndComplianceInfo || {};

  const isManagerWithoutTeams = checkIfManagerWithoutTeams(
    roleType,
    affiliationsByCompany,
    currentCompany,
    isOrgAdminOrHRSpecialist,
  );

  const isWorker = roleType === 'worker';
  const isManager = roleType === 'manager';

  const isW2Employee = isWorker && user?.userEmploymentType === EmployeeType.US_EMPLOYEE;
  const hasW2ContractDocument = (currentContract as any)?.contractDocumentId || currentContract?.contractDocumentID;
  const isW2ContractSigned = (currentContract as any)?.contractFileId || currentContract?.contractFileID;
  const isW2EmployeeWithContract = isW2Employee && Boolean(hasW2ContractDocument) && !isW2ContractSigned;
  const isContractor = isWorker && user?.userEmploymentType === EmployeeType.CONTRACTOR;
  const isEOREmployee = isWorker && user?.userEmploymentType === EmployeeType.EMPLOYEE;
  const { forceSetupInProgress, onboardingFlowComplete: isOnboardingFlowCompleted } = company?.w2Payroll || {};
  // Deliberately blind to whether Check has closed the company. A terminated payroll account keeps
  // its read-only surface (history, paystubs, tax filings, employee info); writes are held back by
  // isPayrollRunnable, which every run route and action button already goes through.
  const isW2Admin = useMemo(
    () => isManager && Boolean(company?.w2Payroll?.checkId),
    [isManager, company?.w2Payroll?.checkId],
  );

  // Every route on this flag lives under /payroll/us-payroll. The W-2 check is what keeps
  // contractor-only companies out: forceSetupInProgress is unset there, so every manager
  // would otherwise get in.
  const isCompanyImplemented = isW2Admin && !forceSetupInProgress;
  const { implementation } = checkCompany || {};
  // Jurisdiction paperwork from hiring in a new state flips Check's onboard status to
  // "blocking" but must not lock live companies out of the payroll run routes - per-run
  // enforcement happens at submission. Only a pending implementation (registration /
  // address verification) or having no employees keeps these routes closed.
  const isImplementationAndOnboardingNotComplete =
    implementation?.status !== 'completed' || checkEmployees?.length === 0;

  const isImplementationAndOnBoardingComplete = isCompanyImplemented && !isImplementationAndOnboardingNotComplete;
  // Terminated / blocked (out of good standing) Check companies can't run payroll - guards the
  // run / off-cycle / bonus routes against direct URL access, not just the disabled buttons.
  const isPayrollRunnable = !isCheckCompanyRestricted(checkCompany);
  const { hasEOREmployees } = company || {};
  const isInSuperAdminMode = user?.isSuperAdmin;
  const canShowManagers = isOrgAdminOrHRSpecialist && (isPayrollEnabled || isAPEnabled || isAREnabled);
  // we want if admin is part of many teams and he has at least one team for which he has permission to review invoices
  // we want to show notifications for him
  const canReviewInvoicesForAnyTeam = useMemo(() => {
    if (!companyPermissions?.teams) return false;
    return Object.keys(companyPermissions.teams).some((teamId) => canReviewInvoiceByTeamId(teamId));
  }, [companyPermissions?.teams, canReviewInvoiceByTeamId]);

  const canShowNotifications = isPayrollEnabled && (isOrgAdmin || isHRSpecialist || canReviewInvoicesForAnyTeam);

  // The worker's own tracked W-2 contract, used to check whether their pay is hourly: timesheets
  // only pay hourly employees, so salaried staff get no time-tracking surfaces at all.
  const workerW2CheckEmployeeId = useMemo(() => {
    // Time tracking is company-gated; skip the Check earning-rate lookup entirely for
    // companies that never enabled it.
    if (currentCompany?.settings?.timeTrackingSettings?.timeTrackingEnabled !== true) return '';
    const affiliation = affiliationsByCompany?.[currentCompany?.companyID];
    const tracked = (affiliation?.contracts ?? []).find(
      (contract) =>
        Boolean(contract?.w2Payroll?.checkCompanyId) &&
        (!contract.terminationDetails?.endDate || !isDateInPast(contract.terminationDetails.endDate)) &&
        contract?.w2Payroll?.timeTrackingEnabled !== false,
    );
    return tracked?.w2Payroll?.checkEmployeeId ?? '';
  }, [affiliationsByCompany, currentCompany]);
  const { defaultEarningRate, isPending: isEarningRatePending } = useCheckAllEarningRates({
    employeeID: workerW2CheckEmployeeId,
  });

  const hasActiveTimeTrackingContract = useMemo(() => {
    if (!user || !affiliationsByCompany) return false;

    const currentCompanyAffiliation = affiliationsByCompany[currentCompany?.companyID];

    const allContracts = currentCompanyAffiliation?.contracts ?? [];
    const activeW2Contracts = allContracts.filter(
      (contract) =>
        Boolean(contract?.w2Payroll?.checkCompanyId) &&
        (!contract.terminationDetails?.endDate || !isDateInPast(contract.terminationDetails.endDate)),
    );

    // W-2 time tracking takes precedence when the employee qualifies for it: it requires the company
    // primary toggle, the employee not being excluded (opt-out: an unset per-employee flag means
    // tracked, mirroring the backend's orElse(true)), and an hourly default-compensation rate (same
    // rule as the Pay tab and the backend's hourly filter). Fail open while the rate loads so hourly
    // employees never lose the nav mid-fetch.
    const qualifiesForW2TimeTracking =
      activeW2Contracts.length > 0 &&
      currentCompany?.settings?.timeTrackingSettings?.timeTrackingEnabled === true &&
      activeW2Contracts.some((contract) => contract?.w2Payroll?.timeTrackingEnabled !== false) &&
      (isEarningRatePending || defaultEarningRate?.period === 'hourly');
    if (qualifiesForW2TimeTracking) return true;

    // Otherwise fall through to the contractor tracker. A user can be both a W-2 employee and hold a
    // separate active TIME_TRACKING contractor contract; that contractor tab must still show even
    // when the W-2 side does not qualify (e.g. the company has not enabled W-2 time tracking).
    return Boolean(
      currentCompanyAffiliation?.contractorContracts?.some(
        (contract) =>
          contract.contractType === ContractType.TIME_TRACKING &&
          (!contract.terminationDetails?.endDate || !isDateInPast(contract.terminationDetails.endDate)),
      ),
    );
  }, [user, affiliationsByCompany, currentCompany, isEarningRatePending, defaultEarningRate]);

  const isTheraAdvanceEligible = useMemo(() => {
    if (isUserLoading || !affiliationsByCompany || !currentCompany) return false; // loading guard - hide until data ready
    if (user?.employeeInformation?.country === 'US') return false;
    const isTpApproved =
      currentCompany?.theraBanking === true &&
      currentCompany?.tp?.payrollFboOnboardingStage === 'DISBURSEMENT_ACCOUNT_APPROVED';
    if (!isTpApproved) return false;
    const contracts = affiliationsByCompany[currentCompany.companyID]?.contracts || [];
    return getEligibleAdvanceContract(contracts) !== null;
  }, [isUserLoading, affiliationsByCompany, currentCompany, user]);

  const isTheraAdvanceCompany = useMemo(() => {
    if (isUserLoading || !currentCompany) return false; // loading guard - hide until data ready
    return currentCompany?.flags?.isTheraAdvanceCompany === true;
  }, [isUserLoading, currentCompany]);

  const isCopilotEnabled = useMemo(() => {
    if (isUserLoading || !currentCompany) return false;
    return currentCompany?.flags?.isCopilotEnabled === true;
  }, [isUserLoading, currentCompany]);

  const isFastPaymentsDisabled = useMemo(() => {
    if (isUserLoading || !currentCompany) return false; // loading guard - hide until data ready
    return currentCompany?.fastPayments === false;
  }, [isUserLoading, currentCompany]);

  // Check if KYC and compliance is enabled for non-US companies
  const isKycAndComplianceEnabled = useMemo(() => {
    if (isUSCompany) return true;

    // Check if either KYC or compliance docs are enabled
    return Boolean(complianceDocsEnabled || kycEnabled);
  }, [isUSCompany, complianceDocsEnabled, kycEnabled]);

  const isTheraCompany = useMemo(() => {
    return isTheraDemoCompany(currentCompany?.companyID || '');
  }, [currentCompany]);

  const isTheraProdCompany = useMemo(() => {
    if (!isStytchProdEnv) return true;
    return currentCompany?.companyID === THERA_PROD_COMPANY_ID || currentCompany?.companyID === GAURAVS_PROD_COMPANYID;
  }, [currentCompany]);

  // Only org admins of the owner company can be WLC admins - skip the network call for everyone
  // else. `isLoading` stays `false` for disabled calls so AuthorizationCheck doesn't block loading.
  const { whiteLabelClients: myWhiteLabelClients, isLoading: isMyWLCLoading } = useMyWhiteLabelClients(
    isManager && isOrgAdmin,
  );
  const isWhiteLabelClientAdmin = myWhiteLabelClients.length > 0;

  const flags = useMemo(
    (): AccessFlags => ({
      w2Admin: Boolean(isPayrollEnabled && isW2Admin && isOnboardingFlowCompleted),
      hasEOREmployees: Boolean(hasEOREmployees),
      isUSCompany,
      isAREnabled: Boolean(isAREnabled),
      isAPEnabled: Boolean(isAPEnabled),
      canEditAR: Boolean(isAREnabled && (isOrgAdmin || isAPARManager)),
      canEditAP: Boolean(isAPEnabled && (isOrgAdmin || isAPARManager)),
      canEditBanking: Boolean(isBankingEnabled && isOrgAdmin),
      isPayrollEnabled: Boolean(isPayrollEnabled),
      // Org chart pilot: only gated in production. Non-prod (staging) shows it to everyone; in prod
      // it's limited to the pilot user IDs. Empty ORG_CHART_PILOT_USER_IDS to release to all of prod.
      isOrgChartEnabled:
        !isStytchProdEnv ||
        ORG_CHART_PILOT_USER_IDS.length === 0 ||
        Boolean(user?.userID && ORG_CHART_PILOT_USER_IDS.includes(user.userID)),
      // Corridor rates exposes provider pricing and payout margins, so production access is by
      // named user only. Note the deliberate difference from the org chart above: an empty list
      // here closes the page rather than opening it, so nobody is added by accident.
      isCorridorRatesEnabled:
        !isStytchProdEnv || Boolean(user?.userID && CORRIDOR_RATES_USER_IDS.includes(user.userID)),
      isPayoutVolumeEnabled: !isStytchProdEnv || Boolean(user?.userID && PAYOUT_VOLUME_USER_IDS.includes(user.userID)),
      managerWithTeams: Boolean(!isManagerWithoutTeams && isPayrollEnabled),
      orgAdmin: isOrgAdmin || isHRSpecialist,
      // Admin and nothing else. An HR specialist counts as orgAdmin above, but issuing a key
      // hands out access to the whole company's data, so that stays with the admin.
      onlyOrgAdmin: isOrgAdmin,
      companyWithEorTracking: false, // Declared in ManagerAccessType but unused - no logic exists
      isCompanyImplemented,
      isImplementationAndOnBoardingComplete,
      isPayrollRunnable,
      isInSuperAdminMode: Boolean(isInSuperAdminMode),
      canShowManagers: Boolean(canShowManagers),
      isBankingEnabled: Boolean(isBankingEnabled),
      canShowNotifications: Boolean(canShowNotifications),
      timeTracking: hasActiveTimeTrackingContract,
      canUserCreateBridgeWallet: Boolean(canUserCreateWallet),
      isCopilotEnabled,
      isTheraDemoCompany: isTheraCompany,
      isTheraProdCompany,
      isWhiteLabelClientAdmin,
      contractor: isContractor,
      eorEmployee: isEOREmployee,
      w2Employee: isW2Employee,
      w2EmployeeWithContract: isW2EmployeeWithContract,
      isKycAndComplianceEnabled,
      isTheraAdvanceEligible,
      isTheraAdvanceCompany,
      isFastPaymentsDisabled,
    }),
    [
      isW2Admin,
      isOrgAdmin,
      isHRSpecialist,
      isAPARManager,
      isManagerWithoutTeams,
      isUSCompany,
      hasEOREmployees,
      isContractor,
      isW2Employee,
      isW2EmployeeWithContract,
      isEOREmployee,
      isCompanyImplemented,
      isImplementationAndOnBoardingComplete,
      isPayrollRunnable,
      isOnboardingFlowCompleted,
      isAREnabled,
      isAPEnabled,
      isBankingEnabled,
      isPayrollEnabled,
      isInSuperAdminMode,
      canShowManagers,
      canShowNotifications,
      hasActiveTimeTrackingContract,
      canUserCreateWallet,
      isKycAndComplianceEnabled,
      isTheraCompany,
      isTheraProdCompany,
      isWhiteLabelClientAdmin,
      isTheraAdvanceEligible,
      isTheraAdvanceCompany,
      isCopilotEnabled,
      isFastPaymentsDisabled,
      user?.userID,
    ],
  );

  return { flags, isMyWLCLoading };
};
